import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  TextField,
  Typography,
  Autocomplete,
  Tab,
  Tabs,
} from "@mui/material";
import { X } from "lucide-react";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckIcon from "@mui/icons-material/Check";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { showSuccess, showError } from "../../../utils/toast";
import getClientChasingSummary from "../../../api/getClientChasingSummary";
import getPendingChasingDocuments from "../../../api/getPendingChasingDocuments";
import getActiveEmailTemplates from "../../../api/getActiveEmailTemplates";
import previewChasingEmail from "../../../api/previewChasingEmail";
import sendChasingEmail from "../../../api/sendChasingEmail";
import getChasingHistory from "../../../api/getChasingHistory";
import EditClientPopup from "../EditClientPopup";

const schema = yup.object().shape({
  template: yup.object().required("Template is required").nullable(),
  recipientEmail: yup
    .string()
    .required("Recipient email is required")
    .email("Enter a valid email address"),
});

// Full-page Document Chasing view (client-wide — shows every pending
// document). Reached via the top-level "Document Chasing" tab button, styled
// to match the Requested/Uploaded pages it sits alongside.
// clientPhone / clientRecord / typeOptions are display-only extras for the
// header (phone, Client ID, Edit Client); all data loading, selection and
// sending below is unchanged.
const DocumentChasingPage = ({ clientID, clientPhone, clientRecord, typeOptions }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [summary, setSummary] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAllOlderHistory, setShowAllOlderHistory] = useState(false);
  const [excludedDocIds, setExcludedDocIds] = useState([]);

  const PENDING_PREVIEW_COUNT = 6;
  const RECENT_DETAILED_COUNT = 1;
  const RECENT_HISTORY_COUNT = 5;
  const OLDER_HISTORY_PREVIEW_COUNT = 3;

  const { token } = useContext(AuthContext);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      template: null,
      recipientEmail: "",
      subjectPreview: "",
      bodyPreview: "",
    },
  });

  const loadData = async () => {
    if (!clientID) return;
    setLoading(true);
    setTabValue(0);
    setShowAllPending(false);
    setExcludedDocIds([]);
    reset();

    const [summaryRes, pendingRes, templatesRes] = await Promise.all([
      getClientChasingSummary({ token, contact_id: clientID }),
      getPendingChasingDocuments({ token, contact_id: clientID }),
      getActiveEmailTemplates({ token }),
    ]);

    if (summaryRes?.code === 200) {
      setSummary(summaryRes.data);
      setValue("recipientEmail", summaryRes.data.clientEmail || "");
    }
    if (pendingRes?.code === 200) {
      setPendingDocs(pendingRes.data || []);
    }
    if (templatesRes?.code === 200) {
      setTemplates(templatesRes.data || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientID]);

  const fetchHistory = async () => {
    setShowAllOlderHistory(false);
    const res = await getChasingHistory({ token, contact_id: clientID });
    setHistory(res?.code === 200 ? res.data || [] : []);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchHistory();
    }
  };

  // Pending docs the user hasn't removed from this send session via the chip's
  // close icon. Removal is local-only — it never deletes/modifies the
  // underlying document, and resets whenever the client/page reloads.
  const visiblePendingDocs = pendingDocs.filter(
    (doc) => !excludedDocIds.includes(doc.document_request_id),
  );
  // Only sent when non-empty, so the default (nothing removed) case hits the
  // API exactly as before — no new param, no change in behavior.
  const excludeDocIdsParam = excludedDocIds.join(",");

  // Shared by template selection and chip removal so the preview always
  // reflects the current exclusion list — takes it as an explicit argument
  // rather than reading `excludedDocIds` from closure, since the caller may
  // need to use a just-updated value before the next render lands.
  const refreshPreview = async (selectedTemplate, excludeIds) => {
    if (!selectedTemplate) {
      setValue("subjectPreview", "");
      setValue("bodyPreview", "");
      return;
    }
    const res = await previewChasingEmail({
      token,
      contact_id: clientID,
      template_id: selectedTemplate.id,
      exclude_document_request_id: excludeIds.join(","),
    });
    if (res?.code === 200) {
      setValue("subjectPreview", res.data.subject);
      setValue("bodyPreview", res.data.body);
    } else {
      showError("Unable to generate preview.");
    }
  };

  const handleTemplateChange = (selectedTemplate) => {
    refreshPreview(selectedTemplate, excludedDocIds);
  };

  const handleRemovePendingDoc = (docId) => {
    const updatedExcludedDocIds = [...excludedDocIds, docId];
    setExcludedDocIds(updatedExcludedDocIds);
    const currentTemplate = getValues("template");
    if (currentTemplate) {
      refreshPreview(currentTemplate, updatedExcludedDocIds);
    }
  };

  const onSubmit = async (data) => {
    setSending(true);
    try {
      const response = await sendChasingEmail({
        token,
        contact_id: clientID,
        template_id: data.template.id,
        recipient_email: data.recipientEmail,
        exclude_document_request_id: excludeDocIdsParam,
      });
      if (response?.code === 200) {
        showSuccess("Reminder email sent successfully.");
        reset();
        loadData();
      } else {
        showError(response?.error || "Error while sending reminder email.");
      }
    } catch (err) {
      showError("Error while sending reminder email.");
    } finally {
      setSending(false);
    }
  };

  // Cancel: discard the current draft (template, previews, recipient edits and
  // removed chips) - local state only, no API call.
  const handleCancel = () => {
    reset({
      template: null,
      recipientEmail: summary?.clientEmail || "",
      subjectPreview: "",
      bodyPreview: "",
    });
    setExcludedDocIds([]);
    setShowAllPending(false);
  };

  // ── Presentation helpers (UI only) ─────────────────────────────────────────
  const TEAL = "#1a6b7c";
  const CHIP_ICON_COLORS = [
    "text-blue-500",
    "text-emerald-500",
    "text-violet-500",
    "text-orange-500",
    "text-red-500",
    "text-sky-600",
  ];
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");

  // Header contact details (display only; empty values are simply omitted).
  const phoneDisplay = String(clientPhone || "").replace(/^(null|undefined)+/i, "").trim();
  const clientRef = clientRecord?.client_reference_number;
  const location = clientRecord?.location || clientRecord?.country || clientRecord?.state;
  const avatarLetter = (summary?.clientName || "?").trim().charAt(0).toUpperCase();

  // Workflow indicator derived from the real page state: pending documents
  // loaded -> steps 1-2 done; template chosen (preview loaded) -> step 4.
  const selectedTemplate = watch("template");
  const hasPending = visiblePendingDocs.length > 0;
  const currentStep = !hasPending ? 1 : selectedTemplate ? 4 : 3;
  const WORKFLOW_STEPS = [
    { title: "Review Pending Documents", subtitle: "Check and select documents to chase" },
    { title: "Select Documents", subtitle: "Choose specific documents" },
    { title: "Choose Email Template", subtitle: "Select and customize template" },
    { title: "Preview and Send", subtitle: "Review content and send email" },
  ];

  const KpiStat = ({ icon: Icon, iconClass, label, value, valueClass = "text-gray-900" }) => (
    <div className="flex min-w-0 items-center gap-2.5 px-3 py-1.5">
      <Icon sx={{ fontSize: 22 }} className={`shrink-0 ${iconClass}`} />
      <div className="min-w-0">
        <div className={`whitespace-nowrap text-sm font-semibold leading-tight ${valueClass}`}>{value}</div>
        <div className="text-[11px] leading-tight text-gray-500">{label}</div>
      </div>
    </div>
  );

  const ContactItem = ({ icon: Icon, children }) => (
    <span className="flex min-w-0 items-center gap-1 text-[12.5px] text-gray-600">
      <Icon sx={{ fontSize: 15 }} className="shrink-0 text-gray-500" />
      <span className="truncate">{children}</span>
    </span>
  );

  const FieldLabel = ({ children, required }) => (
    <div className="mb-1 text-[13px] font-semibold text-gray-800">
      {children}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </div>
  );

  // Compact outlined inputs without floating labels (label is shown above).
  const compactFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#cbd5e1" },
      "&.Mui-focused fieldset": { borderColor: TEAL, borderWidth: "1px" },
    },
    "& .MuiOutlinedInput-input": { padding: "9px 12px" },
  };

  const HistoryItem = ({ item, detailed }) =>
    detailed ? (
      <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed shadow-sm">
        <div>
          <strong>Template:</strong> {item.template_name || "-"}
        </div>
        <div>
          <strong>Subject:</strong> {item.subject}
        </div>
        <div>
          <strong>Sent By:</strong> {item.sent_by}
        </div>
        <div>
          <strong>Sent Date:</strong> {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}
        </div>
        <div>
          <strong>Status:</strong>{" "}
          <span className={item.status === "sent" ? "text-green-600" : "text-red-600"}>{item.status}</span>
        </div>
        {item.status === "failed" && item.error_message && (
          <div className="text-red-600">
            <strong>Error:</strong> {item.error_message}
          </div>
        )}
      </div>
    ) : (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900">{item.template_name || "-"}</div>
          <div className="truncate text-gray-600">{item.subject}</div>
          <div className="text-gray-400">{formatDate(item.created_at)}</div>
        </div>
        <span className={`whitespace-nowrap font-medium ${item.status === "sent" ? "text-green-600" : "text-red-600"}`}>
          {item.status}
        </span>
      </div>
    );

  return (
    <div className="table-content flex flex-col gap-2">
      {/* Client Summary - full width */}
      <div className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e3f0f5] text-lg font-semibold text-[#1a6b7c]">
              {avatarLetter}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="break-words text-lg font-bold leading-tight text-gray-900">
                  {summary?.clientName || "-"}
                </span>
                <span className="rounded-md bg-[#eef5f8] px-1.5 py-px text-[10px] font-semibold text-gray-600">
                  Client Summary
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3.5 gap-y-0.5">
                {summary?.clientEmail && <ContactItem icon={MailOutlineIcon}>{summary.clientEmail}</ContactItem>}
                {phoneDisplay && <ContactItem icon={PhoneOutlinedIcon}>{phoneDisplay}</ContactItem>}
                {location && <ContactItem icon={PlaceOutlinedIcon}>{location}</ContactItem>}
                {clientRef && <ContactItem icon={BadgeOutlinedIcon}>Client ID: {clientRef}</ContactItem>}
              </div>
            </div>
          </div>
          {clientRecord && (
            <EditClientPopup
              tableValues={clientRecord}
              typeOptions={typeOptions || clientRecord.typeOptions || []}
              setClientUpdatedToTrue={loadData}
              renderTrigger={(open) => (
                <button
                  type="button"
                  onClick={open}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  Edit Client
                </button>
              )}
            />
          )}
        </div>

        <div className="mt-2 grid grid-cols-2 rounded-lg border border-gray-200 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-gray-100">
          <KpiStat icon={DescriptionOutlinedIcon} iconClass="text-blue-500" label="Requested" value={summary?.totalRequested ?? "-"} />
          <KpiStat icon={CloudUploadOutlinedIcon} iconClass="text-emerald-500" label="Uploaded" value={summary?.totalUploaded ?? "-"} />
          <KpiStat
            icon={AccessTimeIcon}
            iconClass="text-orange-500"
            label="Pending"
            value={summary?.totalPending ?? "-"}
            valueClass="text-orange-500"
          />
          <KpiStat icon={CalendarMonthOutlinedIcon} iconClass="text-violet-500" label="Last Request" value={formatDate(summary?.lastRequestDate)} />
          <KpiStat icon={CalendarMonthOutlinedIcon} iconClass="text-blue-600" label="Last Received" value={formatDate(summary?.lastReceivedDate)} />
        </div>
      </div>

      <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            px: 1.5,
            minHeight: 40,
            borderBottom: "1px solid #eef0f3",
            "& .MuiTab-root": {
              minHeight: 40,
              paddingTop: "6px",
              paddingBottom: "6px",
              fontSize: "13px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: "#4b5563",
            },
            "& .MuiTab-root.Mui-selected": { color: TEAL },
            "& .MuiTabs-indicator": { backgroundColor: TEAL, height: 2 },
          }}
        >
          <Tab icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Send Email" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Email History" />
        </Tabs>

        {tabValue === 0 && (
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Workflow steps */}
            <ol className="grid grid-cols-2 gap-x-3 gap-y-2 border-b border-[#eef0f3] px-4 py-2 md:grid-cols-4">
              {WORKFLOW_STEPS.map((step, i) => {
                const n = i + 1;
                const state = n < currentStep ? "complete" : n === currentStep ? "active" : "upcoming";
                return (
                  <li key={step.title} className="flex min-w-0 items-center gap-2">
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        state === "active"
                          ? "bg-[#1a6b7c] text-white ring-[3px] ring-[#d6ebf0]"
                          : state === "complete"
                          ? "bg-[#1a6b7c] text-white"
                          : "bg-[#eef1f4] text-gray-500"
                      }`}
                    >
                      {state === "complete" ? <CheckIcon sx={{ fontSize: 15 }} /> : n}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block truncate text-[12.5px] font-semibold leading-tight ${
                          state === "upcoming" ? "text-gray-500" : "text-gray-900"
                        }`}
                      >
                        {step.title}
                      </span>
                      <span className="block truncate text-[11px] leading-tight text-gray-500" title={step.subtitle}>{step.subtitle}</span>
                    </span>
                  </li>
                );
              })}
            </ol>

            <div className="grid grid-cols-1 items-start gap-4 p-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
              {/* Left - Pending Documents */}
              <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <DescriptionOutlinedIcon sx={{ fontSize: 22 }} className="text-[#1a6b7c]" />
                  <span className="text-base font-semibold text-gray-900">Pending Documents</span>
                  {visiblePendingDocs.length > 0 && (
                    <span className="rounded-full bg-[#e6f1f9] px-2 py-0.5 text-[11px] font-semibold text-[#2b6fa8]">
                      {visiblePendingDocs.length} selected
                    </span>
                  )}
                  {visiblePendingDocs.length > PENDING_PREVIEW_COUNT && (
                    <button
                      type="button"
                      onClick={() => setShowAllPending((v) => !v)}
                      className="ml-auto flex items-center gap-0.5 text-xs font-semibold text-[#2b6fa8] hover:underline"
                    >
                      {showAllPending
                        ? "Show Less"
                        : `Show More (${visiblePendingDocs.length - PENDING_PREVIEW_COUNT} more)`}
                      <KeyboardArrowDownIcon
                        sx={{ fontSize: 18 }}
                        className={`transition-transform ${showAllPending ? "rotate-180" : ""}`}
                      />
                    </button>
                  )}
                </div>

                {!loading && visiblePendingDocs.length === 0 ? (
                  <Typography color="text.secondary" sx={{ my: 1, fontSize: 14 }}>
                    No Pending Documents
                  </Typography>
                ) : (
                  <div
                    className={
                      showAllPending
                        ? "flex max-h-[260px] flex-wrap gap-2 overflow-y-auto pr-1"
                        : "flex flex-wrap gap-2"
                    }
                  >
                    {(showAllPending
                      ? visiblePendingDocs
                      : visiblePendingDocs.slice(0, PENDING_PREVIEW_COUNT)
                    ).map((doc, i) => (
                      <span
                        key={doc.document_request_id}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-[#f7f9fb] py-1.5 pl-2.5 pr-2 text-[13px] leading-snug text-gray-800"
                      >
                        <DescriptionOutlinedIcon
                          sx={{ fontSize: 16 }}
                          className={`shrink-0 ${CHIP_ICON_COLORS[i % CHIP_ICON_COLORS.length]}`}
                        />
                        <span className="min-w-0 break-words">{doc.document_name}</span>
                        <X
                          size={14}
                          className="ml-0.5 shrink-0 cursor-pointer text-gray-500 hover:text-gray-900"
                          onClick={() => handleRemovePendingDoc(doc.document_request_id)}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Right - Chase Email */}
              <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2">
                  <MailOutlineIcon sx={{ fontSize: 22 }} className="text-[#1a6b7c]" />
                  <span className="text-base font-semibold text-gray-900">Chase Email</span>
                </div>

                {visiblePendingDocs.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    <div>
                      <FieldLabel required>Recipient Email</FieldLabel>
                      <Controller
                        name="recipientEmail"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            placeholder="Recipient Email"
                            error={!!errors.recipientEmail}
                            helperText={errors.recipientEmail?.message}
                            sx={compactFieldSx}
                            InputProps={{
                              startAdornment: (
                                <PersonOutlineIcon sx={{ fontSize: 18, mr: 1 }} className="text-gray-500" />
                              ),
                            }}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <FieldLabel required>Email Template</FieldLabel>
                      <Controller
                        name="template"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={templates}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            value={field.value || null}
                            onChange={(e, value) => {
                              field.onChange(value);
                              handleTemplateChange(value);
                            }}
                            popupIcon={<KeyboardArrowDownIcon />}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                {option.name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select Template"
                                variant="outlined"
                                error={!!errors.template}
                                helperText={errors.template?.message}
                                sx={compactFieldSx}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <ArticleOutlinedIcon sx={{ fontSize: 18, ml: 0.5, mr: 0.5 }} className="text-gray-500" />
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <FieldLabel>Subject Preview</FieldLabel>
                      <Controller
                        name="subjectPreview"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            placeholder="Subject will appear here..."
                            InputProps={{ readOnly: true }}
                            sx={compactFieldSx}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <FieldLabel>Email Body Preview</FieldLabel>
                      <Controller
                        name="bodyPreview"
                        control={control}
                        render={({ field }) =>
                          field.value ? (
                            <div
                              className="rounded-lg border border-gray-200 bg-white p-3 text-sm"
                              style={{ minHeight: "120px", maxHeight: "260px", overflowY: "auto" }}
                              dangerouslySetInnerHTML={{ __html: field.value }}
                            />
                          ) : (
                            <div
                              className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-400"
                              style={{ minHeight: "120px" }}
                            >
                              Email body preview will appear here...
                            </div>
                          )
                        }
                      />
                    </div>

                    <div className="flex items-center gap-2 rounded-lg bg-[#eef5fb] px-3 py-2 text-[13px] text-gray-700">
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} className="shrink-0 text-[#2b6fa8]" />
                      This email will be sent for {visiblePendingDocs.length} selected document(s).
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outlined"
                        disabled={sending}
                        onClick={handleCancel}
                        sx={{
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "none",
                          color: "#374151",
                          borderColor: "#d1d5db",
                          "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={sending}
                        startIcon={<SendIcon sx={{ fontSize: 16, transform: "rotate(-30deg)" }} />}
                        sx={{
                          backgroundColor: TEAL,
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "none",
                          boxShadow: "0 2px 6px rgba(26,107,124,0.25)",
                          "&:hover": { backgroundColor: "#155e6e" },
                        }}
                      >
                        Send Chase Email
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                    {loading ? "Loading..." : "There are no pending documents to chase for this client."}
                  </Typography>
                )}
              </div>
            </div>
          </form>
        )}

        {tabValue === 1 && (
          <div className="p-4">
            {history?.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Left — most recent (1 detailed + compact) */}
                <div>
                  <div className="mb-2 text-[15px] font-semibold text-gray-900">Recent</div>
                  <div className="flex flex-col gap-2">
                    {history.slice(0, RECENT_DETAILED_COUNT).map((item) => (
                      <HistoryItem key={item.id} item={item} detailed />
                    ))}
                    {history.slice(RECENT_DETAILED_COUNT, RECENT_HISTORY_COUNT).map((item) => (
                      <HistoryItem key={item.id} item={item} />
                    ))}
                  </div>
                </div>

                {/* Right — older, compact */}
                <div>
                  <div className="mb-2 text-[15px] font-semibold text-gray-900">Older</div>
                  {(() => {
                    const olderHistory = history.slice(RECENT_HISTORY_COUNT);
                    if (olderHistory.length === 0) {
                      return (
                        <Typography color="text.secondary" sx={{ my: 1, fontSize: 14 }}>
                          No older history
                        </Typography>
                      );
                    }
                    const visibleOlderHistory = showAllOlderHistory
                      ? olderHistory
                      : olderHistory.slice(0, OLDER_HISTORY_PREVIEW_COUNT);
                    return (
                      <>
                        <div
                          className={
                            showAllOlderHistory
                              ? "flex max-h-[calc(100vh-320px)] flex-col gap-2 overflow-y-auto pr-1"
                              : "flex flex-col gap-2"
                          }
                        >
                          {visibleOlderHistory.map((item) => (
                            <HistoryItem key={item.id} item={item} />
                          ))}
                        </div>
                        {olderHistory.length > OLDER_HISTORY_PREVIEW_COUNT && (
                          <button
                            type="button"
                            onClick={() => setShowAllOlderHistory((v) => !v)}
                            className="mt-2 flex items-center gap-0.5 text-xs font-semibold text-[#2b6fa8] hover:underline"
                          >
                            {showAllOlderHistory
                              ? "Show Less"
                              : `Show More (${olderHistory.length - OLDER_HISTORY_PREVIEW_COUNT} more)`}
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: 18 }}
                              className={`transition-transform ${showAllOlderHistory ? "rotate-180" : ""}`}
                            />
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <span className="text-sm text-gray-500">No history yet</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentChasingPage;
